import { useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, CheckCircle2, AlertCircle, ImageOff, Search, RotateCw } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import { AdminShell } from '@/components/admin-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ImageLightbox } from '@/components/image-lightbox';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/components/ui/empty';

interface QueueItem {
  id: string;
  tenantId: string;
  tenantName: string;
  direction: 'purchase' | 'sale';
  invoiceNumber: string;
  senderRazonSocial: string;
  senderRuc: string;
  issueDate: string;
  grandTotal: string;
  currency: string;
  status: string;
  extractionProvider: 'gemini' | 'claude' | 'manual' | null;
  extractionPrecision: number | null;
  requiresHumanReviewReason: string | null;
  originalFileId: string | null;
  createdAt: string;
}

// `pending` = meta-filter del backend, agrupa pending_extraction +
// pending_review. El contador ve TODO lo que requiere atención
// (incluyendo facturas que todavía esperan el LLM) en una sola cola.
const STATUSES = ['pending', 'approved', 'rejected'] as const;
const STATUS_LABELS: Record<(typeof STATUSES)[number], string> = {
  pending: 'Pendientes',
  approved: 'Aprobadas',
  rejected: 'Rechazadas',
};

export function ReviewQueuePage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useCurrentAdmin();
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('pending');
  const [page, setPage] = useState(1);
  const [viewerSrc, setViewerSrc] = useState<string | null>(null);
  const pageSize = 25;
  const qc = useQueryClient();

  const bulkReExtract = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; requeued: number }>('/api/admin/invoices/bulk-re-extract', { method: 'POST' }),
    onSuccess: (r) => {
      if (r.requeued === 0) {
        toast.info('No hay facturas atascadas (o llevan menos de 30 min procesando)');
      } else {
        toast.success(`${r.requeued} factura${r.requeued === 1 ? '' : 's'} reencolada${r.requeued === 1 ? '' : 's'} para reprocesar`);
      }
      void qc.invalidateQueries({ queryKey: ['review-queue'] });
    },
    onError: () => toast.error('No se pudo re-encolar las facturas'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['review-queue', status, page],
    queryFn: () => {
      const qs = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize) });
      return api<{ items: QueueItem[]; total: number }>(`/api/admin/invoices?${qs.toString()}`);
    },
    enabled: !!me,
    refetchInterval: 30_000,
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/login');
    return null;
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <AdminShell>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Cola de revisión</h1>
          <p className="text-sm text-muted-foreground">
            Facturas extraídas por IA esperando validación humana. Click en una para abrir el editor.
          </p>
        </div>
        {status === 'pending' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => bulkReExtract.mutate()}
            disabled={bulkReExtract.isPending}
            title="Re-encola todas las facturas atascadas en 'Procesando' hace más de 30 min"
          >
            <RotateCw className={`size-4 mr-2 ${bulkReExtract.isPending ? 'animate-spin' : ''}`} />
            Reprocesar atascadas
          </Button>
        )}
      </div>

      <Tabs value={status} onValueChange={(v) => { setStatus(v as typeof status); setPage(1); }} className="mb-4">
        <TabsList className="inline-flex w-auto">
          {STATUSES.map((s) => (
            <TabsTrigger key={s} value={s}>
              {STATUS_LABELS[s]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="rounded-lg border border-border bg-background shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14"></TableHead>
              <TableHead>Recibida</TableHead>
              <TableHead>Contacto</TableHead>
              <TableHead>Proveedor</TableHead>
              <TableHead>Nº</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>IA</TableHead>
              <TableHead>Alerta</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                  Cargando…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Empty>
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <CheckCircle2 className="text-success" />
                      </EmptyMedia>
                      <EmptyTitle>
                        {status === 'pending' ? 'Todo al día' : 'Sin facturas'}
                      </EmptyTitle>
                      <EmptyDescription>
                        {status === 'pending'
                          ? 'No hay facturas esperando revisión.'
                          : 'Sin facturas en este estado.'}
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            )}
            {data?.items.map((row) => (
              <TableRow
                key={row.id}
                className="hover:bg-accent cursor-pointer"
                onClick={() => navigate(`/admin/invoices/${row.id}`)}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {row.originalFileId ? (
                    <button
                      type="button"
                      onClick={() => setViewerSrc(`/api/admin/files/${row.originalFileId}`)}
                      className="group relative flex size-10 items-center justify-center overflow-hidden rounded-md bg-muted ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      title="Ver factura"
                    >
                      <img
                        src={`/api/admin/files/${row.originalFileId}`}
                        alt=""
                        className="size-full object-cover transition group-hover:opacity-80"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <Search className="absolute size-4 text-white opacity-0 drop-shadow transition group-hover:opacity-100" />
                    </button>
                  ) : (
                    <div className="flex size-10 items-center justify-center rounded-md bg-muted">
                      <ImageOff className="size-4 text-muted-foreground" />
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                  {new Date(row.createdAt).toLocaleString('es-PY', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </TableCell>
                <TableCell className="font-medium text-foreground whitespace-nowrap">
                  {row.tenantName}
                </TableCell>
                <TableCell className="max-w-[20rem]">
                  <div className="truncate" title={row.senderRazonSocial}>
                    {row.senderRazonSocial}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{row.senderRuc}</div>
                </TableCell>
                <TableCell className="font-mono text-xs">{row.invoiceNumber}</TableCell>
                <TableCell className="text-right font-mono">
                  {row.currency} {Number(row.grandTotal).toLocaleString('es-PY')}
                </TableCell>
                <TableCell>
                  {row.extractionProvider ? (
                    <Badge
                      variant={
                        (row.extractionPrecision ?? 0) >= 85
                          ? 'success'
                          : (row.extractionPrecision ?? 0) >= 70
                          ? 'warning'
                          : 'destructive'
                      }
                    >
                      {row.extractionPrecision ?? '–'}%
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">manual</span>
                  )}
                </TableCell>
                <TableCell>
                  {row.requiresHumanReviewReason && (
                    <Badge variant="warning" className="text-[10px]">
                      <AlertCircle className="size-3" />
                      {row.requiresHumanReviewReason}
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {data && data.total > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, data.total)} de {data.total}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Siguiente
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}

      {/* Vista previa rápida de la factura (sin salir de la cola) */}
      <Dialog open={!!viewerSrc} onOpenChange={(open) => !open && setViewerSrc(null)}>
        <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:max-w-3xl">
          <DialogTitle className="border-b border-border px-4 py-3 text-base">
            Vista previa de la factura
          </DialogTitle>
          <div className="h-[78vh]">
            {viewerSrc && <ImageLightbox src={viewerSrc} alt="Factura" className="h-full" />}
          </div>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
